import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { reportApi, reportKeys } from '../api';

/**
 * Task nào phục vụ mục tiêu nào — spec 16.4.
 *
 * ## Vì sao thanh xếp tầng chứ không phải sankey
 *
 * Sankey đẹp khi có mười nút. Một dự án thật có một tá mục tiêu và hàng trăm
 * task, và sankey ở quy mô đó là một búi dây không đọc được. Thứ **hành động
 * được** ở đây chỉ có hai: mục tiêu nào chưa ai làm gì, và mục tiêu nào đang
 * chạy tới đâu — cả hai đọc được ngay trên một thanh xếp tầng.
 *
 * Con số quan trọng nhất là số mục tiêu **chưa có task nào phục vụ**: nó không
 * hiện ra ở bất kỳ báo cáo tiến độ nào khác. Mọi task đúng hạn, mọi biểu đồ
 * xanh, mà mục tiêu vẫn trượt.
 */
export function ObjectiveTrace({
  projectId,
  asOf,
}: {
  projectId: string;
  asOf?: string | undefined;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.objectiveTrace(projectId, asOf ?? ''),
    queryFn: () => reportApi.objectiveTrace(projectId, asOf),
  });

  const objectives = report.data?.objectives ?? [];
  const orphans = report.data?.orphanCount ?? 0;

  return (
    <ChartFrame
      title={t('reports.objectivesTitle')}
      help="objectives"
      subtitle={
        orphans === 0
          ? t('reports.objectivesAllServed')
          : t('reports.objectivesSubtitle', { orphans })
      }
      asOf={asOf ? new Date(asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && objectives.length === 0
          ? { title: t('reports.objectivesEmpty'), description: t('reports.objectivesEmptyHelp') }
          : null
      }
      legend={[
        { label: t('rag.green'), color: FLOW_VAR.done },
        { label: t('reports.remaining'), color: FLOW_VAR.inProgress },
        { label: t('reports.objectiveOrphan'), color: RAG_VAR.red },
      ]}
    >
      <div className="flex flex-col gap-1.5">
        {objectives.map((objective) => (
          <div key={objective.objectiveId} className="flex flex-col gap-0.5">
            <div className="flex items-baseline gap-2 text-xs">
              <span className="truncate font-medium">{objective.title}</span>
              <span className="ml-auto shrink-0 text-muted-foreground">
                {objective.taskCount === 0
                  ? t('reports.objectiveOrphan')
                  : t('chart.itemCount', { count: objective.taskCount })}
              </span>
            </div>

            {objective.taskCount === 0 ? (
              <div className="h-2 rounded-full" style={{ backgroundColor: RAG_VAR.red }} />
            ) : (
              <div className="flex h-2 overflow-hidden rounded-full">
                {(
                  [
                    ['done', objective.doneCount, FLOW_VAR.done],
                    ['inProgress', objective.inProgressCount, FLOW_VAR.inProgress],
                    ['todo', objective.todoCount, FLOW_VAR.todo],
                  ] as const
                ).map(([key, count, color]) => (
                  <span
                    key={key}
                    style={{
                      width: `${(count / objective.taskCount) * 100}%`,
                      backgroundColor: color,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {(report.data?.unlinkedTaskCount ?? 0) > 0 && (
          // Nhiều quá thì traceability chỉ là hình thức: mục tiêu có task, nhưng
          // phần lớn công sức của đội không phục vụ mục tiêu nào.
          <p className="pt-1 text-xs text-muted-foreground">
            {t('reports.objectivesUnlinked', { count: report.data!.unlinkedTaskCount })}
          </p>
        )}
      </div>
    </ChartFrame>
  );
}
